import React, { useState, useMemo, useEffect } from "react";
import { Card, Table, Typography, Space, Tag, Input, InputNumber, message, Switch, Select, Alert } from "antd";
import { ClockCircleOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Sourcing, BidEvaluationResult, SourcingStatus, EvaluationPhase, TechnicalEvaluationMode, EvaluationCriteriaType, EvaluationStatus } from "../interface";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { SAVE_EVALUATION_MUTATION, GET_BIDS_BY_SOURCING_QUERY, UPDATE_BID_EVALUATION_MUTATION } from "../queries";

// Status order for index-based logic
const SOURCING_STATUS_ORDER = [
  SourcingStatus.DRAFT,
  SourcingStatus.SUBMITTED,
  SourcingStatus.APPROVED,
  SourcingStatus.PUBLISHED,
  SourcingStatus.CLOSED,
  SourcingStatus.REJECTED,
  SourcingStatus.RESULT_SUBMITTED,
  SourcingStatus.AWARD_APPROVED,
  SourcingStatus.AWARDED,
];

const { Text, Title } = Typography;

interface EvaluationMatrixProps {
  record: Sourcing;
  viewType?: 'technical' | 'financial';
  isFinancialCompleted?: boolean;
}

interface EvaluationRow {
  criteriaId: string;
  criteriaName: string;
  weight: number;
  maxScore?: number;
  [key: string]: any; // For dynamic supplier columns
}

export const EvaluationMatrix: React.FC<EvaluationMatrixProps> = ({ record, viewType, isFinancialCompleted = false }) => {
  const [evaluationData, setEvaluationData] = useState<Record<string, BidEvaluationResult[]>>({});
  const [bids, setBids] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [supplierEvaluations, setSupplierEvaluations] = useState<Record<string, {compliance: boolean, decision: string, remarks: string}>>({});
  const [supplierToBidMap, setSupplierToBidMap] = useState<Record<string, string>>({});

  // Calculate status index for simplified logic
  const statusIndex = SOURCING_STATUS_ORDER.indexOf(record.status);

  const { mutate: saveEvaluation, isLoading: savingMutation } = useCustomMutation();
  const { mutate: updateBidEvaluation } = useCustomMutation();

  // Query for bids - only enable when status is CLOSED
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && statusIndex === SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED),
    },
  });

  // Load bids data when component mounts or sourcing status changes
  useEffect(() => {
    if (statusIndex === SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED) && record?.id) {
      loadBidsData();
    }
  }, [statusIndex, record?.id]);

  const loadBidsData = async () => {
    if (!record?.id || !refetchBids) return;

    setLoading(true);
    try {
      const response = await refetchBids();
      const bidsData = (response as any)?.data?.data?.getBidsBySourcing || [];

      // Store bids data
      setBids(bidsData);

      // Initialize evaluation data from bid records
      const initialData: Record<string, BidEvaluationResult[]> = {};
      const supplierToBid: Record<string, string> = {};
      bidsData.forEach((bid: any) => {
        initialData[bid._id] = bid.evaluationResults || [];
        supplierToBid[bid.supplierId] = bid._id;
      });
      setEvaluationData(initialData);
      setSupplierToBidMap(supplierToBid);

      // Compute auto-filled supplier evaluations based on total scores
      const evaluations: Record<string, { compliance: boolean; decision: string; remarks: string }> = {};
      const totalScores: { bidId: string; score: number }[] = [];

      bidsData.forEach((bid: any) => {
        const bidId = bid._id;
        const total = record.evaluationCriterias?.reduce((sum, criteria) => {
          const evalResult = initialData[bidId]?.find(er => er.criteriaId === criteria.id);
          const weightedScore = ((evalResult?.value || 0) * criteria.weight) / 100;
          return sum + weightedScore;
        }, 0) || 0;
        totalScores.push({ bidId, score: Math.round(total) });
      });

      const maxScore = Math.max(...totalScores.map(t => t.score));
      const minThreshold = record.evaluationCriterias?.reduce((sum, criteria) => sum + (criteria.threshold || 0), 0) || 0;

      bidsData.forEach((bid: any) => {
        const bidId = bid._id;
        const score = totalScores.find(t => t.bidId === bidId)?.score || 0;
        const compliance = score >= minThreshold; // Compliance based on meeting minimum threshold
        const decision = score === maxScore ? 'Winner' : 'Qualified';
        let remarks = '';
        if (decision === 'Winner') {
          remarks = 'Best overall score';
        } else if (score >= minThreshold * 0.9) {
          remarks = 'Strong technical proposal';
        } else if (score >= minThreshold * 0.7) {
          remarks = 'Acceptable proposal';
        } else {
          remarks = 'Failed technical minimum';
        }

        // Override with existing values if present
        evaluations[bidId] = {
          compliance: bid.compliance !== null && bid.compliance !== undefined ? Boolean(bid.compliance) : compliance,
          decision: bid.decision || decision,
          remarks: bid.evaluationRemarks || remarks,
        };
      });

      setSupplierEvaluations(evaluations);
    } catch (error) {
      console.error('Error loading bids data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Separate technical and financial criteria
  const technicalCriteria = useMemo(() =>
    record.evaluationCriterias?.filter(c => c.type === EvaluationCriteriaType.TECHNICAL) || [],
    [record.evaluationCriterias]
  );

  const financialCriteria = useMemo(() =>
    record.evaluationCriterias?.filter(c => c.type === EvaluationCriteriaType.FINANCIAL) || [],
    [record.evaluationCriterias]
  );

  // Create table data for technical criteria
  const technicalTableData = useMemo(() => {
    if (!technicalCriteria.length) return [];

    const rows: EvaluationRow[] = technicalCriteria.map(criteria => {
      const row: EvaluationRow = {
        criteriaId: criteria.id,
        criteriaName: criteria.name,
        weight: criteria.weight,
      };

      // Calculate max score for this criteria
      const scores = bids.map(bid => {
        const evalResult = evaluationData[bid._id]?.find(er => er.criteriaId === criteria.id);
        return evalResult?.value || 0;
      });
      row.maxScore = Math.max(...scores);

      // Add columns for each supplier
      bids.forEach(bid => {
        const evalResult = evaluationData[bid._id]?.find(er => er.criteriaId === criteria.id);
        row[`${bid._id}_value`] = evalResult?.value || 0;
        row[`${bid._id}_remarks`] = evalResult?.remarks || '';
        row[`${bid._id}_passed`] = evalResult?.passed;
        row[`${bid._id}_evaluatedBy`] = evalResult?.evaluatedBy;
        row[`${bid._id}_evaluatedAt`] = evalResult?.evaluatedAt;
      });

      return row;
    });

    // Add technical score row
    const technicalScoreRow: EvaluationRow = {
      criteriaId: 'technicalScore',
      criteriaName: 'Technical Score',
      weight: 100,
    };

    bids.forEach(bid => {
      const technicalScore = record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL
        ? (bids.find(b => b._id === bid._id)?.technicalScore || 0)
        : technicalCriteria.reduce((sum, criteria) => {
            const evalResult = evaluationData[bid._id]?.find(er => er.criteriaId === criteria.id);
            const weightedScore = ((evalResult?.value || 0) * (criteria.weight || 0)) / 100;
            return sum + weightedScore;
          }, 0);
      technicalScoreRow[`${bid._id}_value`] = Math.round(technicalScore);
      technicalScoreRow[`${bid._id}_remarks`] = '';
    });

    return [...rows, technicalScoreRow];
  }, [technicalCriteria, bids, evaluationData, record.technicalEvaluationMode]);

  // Create table data for financial criteria
  const financialTableData = useMemo(() => {
    if (!financialCriteria.length) return [];

    const rows: EvaluationRow[] = financialCriteria.map(criteria => {
      const row: EvaluationRow = {
        criteriaId: criteria.id,
        criteriaName: criteria.name,
        weight: criteria.weight,
      };

      // Calculate max score for this criteria
      const scores = bids.map(bid => {
        const evalResult = evaluationData[bid._id]?.find(er => er.criteriaId === criteria.id);
        return evalResult?.value || 0;
      });
      row.maxScore = Math.max(...scores);

      // Add columns for each supplier
      bids.forEach(bid => {
        const evalResult = evaluationData[bid._id]?.find(er => er.criteriaId === criteria.id);
        row[`${bid._id}_value`] = evalResult?.value || 0;
        row[`${bid._id}_remarks`] = evalResult?.remarks || '';
        row[`${bid._id}_passed`] = evalResult?.passed;
        row[`${bid._id}_evaluatedBy`] = evalResult?.evaluatedBy;
        row[`${bid._id}_evaluatedAt`] = evalResult?.evaluatedAt;
      });

      return row;
    });

    // Add financial score row
    const financialScoreRow: EvaluationRow = {
      criteriaId: 'financialScore',
      criteriaName: 'Financial Score',
      weight: 100,
    };

    bids.forEach(bid => {
      const financialScore = financialCriteria.reduce((sum, criteria) => {
        const evalResult = evaluationData[bid._id]?.find(er => er.criteriaId === criteria.id);
        const weightedScore = ((evalResult?.value || 0) * (criteria.weight || 0)) / 100;
        return sum + weightedScore;
      }, 0);
      financialScoreRow[`${bid._id}_value`] = Math.round(financialScore);
      financialScoreRow[`${bid._id}_remarks`] = '';
    });

    return [...rows, financialScoreRow];
  }, [financialCriteria, bids, evaluationData]);

  // Summary table data
  const summaryTableData = useMemo(() => {
    const rows: EvaluationRow[] = [];

    // Final score row
    const finalScoreRow: EvaluationRow = {
      criteriaId: 'finalScore',
      criteriaName: 'Final Score',
      weight: 100,
    };

    bids.forEach(bid => {
      const bidData = bids.find(b => b._id === bid._id);
      finalScoreRow[`${bid._id}_value`] = bidData?.finalScore || 0;
      finalScoreRow[`${bid._id}_remarks`] = '';
    });
    rows.push(finalScoreRow);

    // Evaluation status row
    const statusRow: EvaluationRow = {
      criteriaId: 'evaluationStatus',
      criteriaName: 'Status',
      weight: 0,
    };

    bids.forEach(bid => {
      const bidData = bids.find(b => b._id === bid._id);
      statusRow[`${bid._id}_value`] = 0;
      statusRow[`${bid._id}_remarks`] = bidData?.evaluationStatus || '';
    });
    rows.push(statusRow);

    // Compliance row
    const complianceRow: EvaluationRow = {
      criteriaId: 'compliance',
      criteriaName: 'Compliance',
      weight: 0,
    };
    bids.forEach(bid => {
      complianceRow[`${bid._id}_value`] = supplierEvaluations[bid._id]?.compliance ? 1 : 0;
      complianceRow[`${bid._id}_remarks`] = '';
    });
    rows.push(complianceRow);

    // Decision row
    const decisionRow: EvaluationRow = {
      criteriaId: 'decision',
      criteriaName: 'Decision',
      weight: 0,
    };
    bids.forEach(bid => {
      decisionRow[`${bid._id}_value`] = 0;
      decisionRow[`${bid._id}_remarks`] = supplierEvaluations[bid._id]?.decision || '';
    });
    rows.push(decisionRow);

    // Remarks row
    const remarksRow: EvaluationRow = {
      criteriaId: 'evaluationRemarks',
      criteriaName: 'Remarks',
      weight: 0,
    };
    bids.forEach(bid => {
      remarksRow[`${bid._id}_value`] = 0;
      remarksRow[`${bid._id}_remarks`] = supplierEvaluations[bid._id]?.remarks || '';
    });
    rows.push(remarksRow);

    return rows;
  }, [bids, supplierEvaluations]);

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading evaluation data...
        </div>
      </Card>
    );
  }

  const updateEvaluationData = (bidId: string, criteriaId: string, updates: Partial<BidEvaluationResult>) => {
    const newData = { ...evaluationData };
    if (!newData[bidId]) {
      newData[bidId] = [];
    }

    const existingIndex = newData[bidId].findIndex(er => er.criteriaId === criteriaId);
    if (existingIndex >= 0) {
      newData[bidId][existingIndex] = { ...newData[bidId][existingIndex], ...updates };
    } else {
      newData[bidId].push({
        criteriaId,
        value: updates.value || 0,
        remarks: updates.remarks || '',
      });
    }

    setEvaluationData(newData);
  };

  const handleValueChange = (bidId: string, criteriaId: string, value: number | null) => {
    updateEvaluationData(bidId, criteriaId, { value: value || 0 });
  };

  const handleRemarksChange = (bidId: string, criteriaId: string, remarks: string) => {
    updateEvaluationData(bidId, criteriaId, { remarks });
  };

  const updateCompliance = (supplierId: string, compliance: boolean) => {
    const bidId = supplierToBidMap[supplierId];
    if (!bidId) return;

    // Update state
    setSupplierEvaluations(prev => ({
      ...prev,
      [bidId]: { ...prev[bidId], compliance }
    }));

    // Save with the new value immediately (avoid race condition)
    const currentEvaluation = supplierEvaluations[bidId];
    handleSaveBidEvaluationWithValue(bidId, { ...currentEvaluation, compliance });
  };

  const updateDecision = (supplierId: string, decision: string) => {
    const bidId = supplierToBidMap[supplierId];
    if (!bidId) return;
    setSupplierEvaluations(prev => ({
      ...prev,
      [bidId]: { ...prev[bidId], decision }
    }));
  };

  const updateRemarks = (supplierId: string, remarks: string) => {
    const bidId = supplierToBidMap[supplierId];
    if (!bidId) return;
    setSupplierEvaluations(prev => ({
      ...prev,
      [bidId]: { ...prev[bidId], remarks }
    }));
  };

  const handleSaveEvaluation = (bidId: string) => {
    const evaluationResults = evaluationData[bidId] || [];

    // Remove _id field from evaluation results before sending to mutation
    const cleanEvaluationResults = evaluationResults.map((result: any) => ({
      criteriaId: result.criteriaId,
      value: result.value,
      remarks: result.remarks,
    }));

    saveEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
            bidId,
            evaluationResults: cleanEvaluationResults,
          },
        },
        meta: { gqlMutation: SAVE_EVALUATION_MUTATION },
      },
      {
        onSuccess: () => {
          message.success('Evaluation saved successfully');
        },
        onError: (error: any) => {
          message.error('Failed to save evaluation');
          console.error('Save evaluation error:', error);
        },
      },
    );
  };

  const handleSaveBidEvaluation = (bidId: string) => {
    const evaluation = supplierEvaluations[bidId];
    if (!evaluation) return;
    handleSaveBidEvaluationWithValue(bidId, evaluation);
  };

  const handleSaveBidEvaluationWithValue = (bidId: string, evaluation: { compliance: boolean; decision: string; remarks: string }) => {
    const payload = {
      input: {
        bidId,
        compliance: evaluation.compliance,
        decision: evaluation.decision,
        evaluationRemarks: evaluation.remarks,
      },
    };

    updateBidEvaluation(
      {
        url: "",
        method: "post",
        values: payload,
        meta: { gqlMutation: UPDATE_BID_EVALUATION_MUTATION },
      },
      {
        onSuccess: () => {
          // Optional: show success message
        },
        onError: (error: any) => {
          console.error('Save bid evaluation error:', error);
        },
      },
    );
  };


  // Check if financial evaluation should be disabled
  const isFinancialDisabled = (record.technicalEvaluationMode !== TechnicalEvaluationMode.NONE &&
    record.evaluationPhase !== EvaluationPhase.TECHNICAL_COMPLETED &&
    record.evaluationPhase !== EvaluationPhase.FINANCIAL_IN_PROGRESS &&
    record.evaluationPhase !== EvaluationPhase.FINANCIAL_COMPLETED &&
    record.evaluationPhase !== EvaluationPhase.COMPLETE) || isFinancialCompleted;

  // Check if technical evaluation should be disabled
  const isTechnicalDisabled = record.evaluationPhase === EvaluationPhase.TECHNICAL_COMPLETED ||
    record.evaluationPhase === EvaluationPhase.FINANCIAL_IN_PROGRESS ||
    record.evaluationPhase === EvaluationPhase.FINANCIAL_COMPLETED ||
    record.evaluationPhase === EvaluationPhase.COMPLETE;

  const createColumns = (isFinancial: boolean = false) => [
    {
      title: 'Criteria',
      dataIndex: 'criteriaName',
      key: 'criteriaName',
      fixed: 'left' as const,
      width: 200,
      render: (name: string, record: EvaluationRow) => (
        <div style={{ fontWeight: (record.criteriaId.includes('Score') || record.criteriaId === 'evaluationStatus' || record.criteriaId === 'compliance' || record.criteriaId === 'decision' || record.criteriaId === 'evaluationRemarks') ? 'bold' : 'normal' }}>
          {name}
        </div>
      )
    },
    {
      title: 'Weight (%)',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (weight: number, record: EvaluationRow) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {record.criteriaId.includes('Score') || record.criteriaId === 'evaluationStatus' || record.criteriaId === 'compliance' || record.criteriaId === 'decision' || record.criteriaId === 'evaluationRemarks' ? '' : `${weight}%`}
        </Text>
      )
    },
    // Dynamic columns for each supplier
    ...bids.map(bid => ({
      title: bid.companyName || 'Unknown Supplier',
      dataIndex: `${bid._id}_combined`,
      key: `${bid._id}_combined`,
      width: 250,
      render: (value: any, record: EvaluationRow) => {
        const scoreValue = record[`${bid._id}_value`];
        const remarksValue = record[`${bid._id}_remarks`];
        const passed = record[`${bid._id}_passed`];
        const evaluatedBy = record[`${bid._id}_evaluatedBy`];
        const evaluatedAt = record[`${bid._id}_evaluatedAt`];

        if (record.criteriaId.includes('Score') || record.criteriaId === 'finalScore') {
          const bidData = bids.find(b => b._id === bid._id);
          let displayValue = scoreValue;

      let statusColor = '';

          if (record.criteriaId === 'technicalScore') {
            displayValue = bidData?.technicalScore 
          } else if (record.criteriaId === 'financialScore') {
            displayValue = bidData?.financialScore 
          } else if (record.criteriaId === 'finalScore') {
            displayValue = bidData?.finalScore 
          }


          // Color coding for scores
          if (displayValue >= 80) statusColor = 'success';
          else if (displayValue >= 60) statusColor = 'warning';
          else statusColor = 'error';

          return (
            <div style={{ textAlign: 'center' }}>
              {!!displayValue &&<Tag color={statusColor} style={{ fontSize: '14px', padding: '4px 8px' }}>
                {displayValue}/100
              </Tag>
        }
            </div>
          );
        }

        if (record.criteriaId === 'evaluationStatus') {
          const bidData = bids.find(b => b._id === bid._id);
          const status = bidData?.evaluationStatus;
          const statusColor = status === EvaluationStatus.QUALIFIED ? 'success' : status === EvaluationStatus.DISQUALIFIED ? 'error' : 'default';
          return (
            <div style={{ textAlign: 'center' }}>
              <Tag color={statusColor}>
                {status || 'Not Evaluated'}
              </Tag>
            </div>
          );
        }

        if (record.criteriaId === 'compliance') {
          return (
            <div style={{ textAlign: 'center' }}>
              <Switch
                checked={supplierEvaluations[bid._id]?.compliance ?? false}
                onChange={(checked) => updateCompliance(bid.supplierId, checked)}
                disabled={isFinancial && isFinancialDisabled}
              />
            </div>
          );
        }

        if (record.criteriaId === 'decision') {
          return (
            <Select
              value={supplierEvaluations[bid._id]?.decision}
              onChange={(value) => updateDecision(bid.supplierId, value)}
              onBlur={() => handleSaveBidEvaluation(bid._id)}
              style={{ width: '100%' }}
              disabled={isFinancial && isFinancialDisabled}
              options={[
                { value: 'Winner', label: '🏆 Winner' },
                { value: 'Qualified', label: 'Qualified' },
                { value: 'Disqualified', label: 'Disqualified' }
              ]}
            />
          );
        }

        if (record.criteriaId === 'evaluationRemarks') {
          return (
            <Input.TextArea
              value={supplierEvaluations[bid._id]?.remarks}
              onChange={(e) => updateRemarks(bid.supplierId, e.target.value)}
              onBlur={() => handleSaveBidEvaluation(bid._id)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ width: '100%' }}
              disabled={isFinancial && isFinancialDisabled}
            />
          );
        }

        // Regular criteria rows
        const maxScore = record.maxScore || 0;
        let color = '';
        if (scoreValue === maxScore && maxScore > 0) {
          color = 'success'; // green
        } else if (scoreValue < 0.8 * maxScore) {
          color = 'error'; // red
        } else if (scoreValue < 0.9 * maxScore) {
          color = 'warning'; // orange
        }

        const isPassFail = record.criteriaId.startsWith('technical') && record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL;
        const weightedScore = record.criteriaId.includes('Score') ? scoreValue : Math.round((scoreValue * record.weight) / 100);

        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            {isPassFail ? (
              <div style={{ textAlign: 'center' }}>
                <Space>
                  {passed ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                  <Text>{passed ? 'Pass' : 'Fail'}</Text>
                </Space>
              </div>
            ) : (
              <InputNumber
                min={0}
                max={100}
                value={scoreValue}
                onChange={(val) => handleValueChange(bid._id, record.criteriaId, val)}
                onBlur={() => handleSaveEvaluation(bid._id)}
                style={{ width: '100%' }}
                disabled={record.criteriaId.includes('Score') || (isFinancial && isFinancialDisabled) || (!isFinancial && isTechnicalDisabled)}
                addonAfter={!record.criteriaId.includes('Score') ? `${weightedScore}/${record.weight} pts` : undefined}
              />
            )}
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                {color && !isPassFail && <Tag color={color} />}
                <Input
                  value={remarksValue}
                  onChange={(e) => handleRemarksChange(bid._id, record.criteriaId, e.target.value)}
                  onBlur={() => handleSaveEvaluation(bid._id)}
                  placeholder="Remarks"
                  size="small"
                  disabled={record.criteriaId.includes('Score') || (isFinancial && isFinancialDisabled) || (!isFinancial && isTechnicalDisabled)}
                  style={{ flex: 1 }}
                />
              </Space>
              {evaluatedBy && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  <Space>
                    <UserOutlined />
                    <Text type="secondary">{evaluatedBy}</Text>
                    {evaluatedAt && (
                      <>
                        <ClockCircleOutlined />
                        <Text type="secondary">{new Date(evaluatedAt).toLocaleDateString()}</Text>
                      </>
                    )}
                  </Space>
                </div>
              )}
            </Space>
          </Space>
        );
      }
    }))
  ];

  const technicalColumns = createColumns(false);
  const financialColumns = createColumns(true);
  const summaryColumns = createColumns(false);

  if (!record.evaluationCriterias || record.evaluationCriterias.length === 0) {
    return (
      <Card title="Evaluation Matrix" type="inner">
        <Text type="secondary">No evaluation criteria defined for this sourcing.</Text>
      </Card>
    );
  }

  if (bids.length === 0) {
    return (
      <Card title="Evaluation Matrix" type="inner">
        <Text type="secondary">No bids available for evaluation.</Text>
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Phase Indicator */}
      <Card size="small">
        <Space>
          <Text strong>Evaluation Phase:</Text>
          <Tag color={
            record.evaluationPhase === EvaluationPhase.NOT_STARTED ? 'default' :
            record.evaluationPhase === EvaluationPhase.TECHNICAL_IN_PROGRESS ? 'processing' :
            record.evaluationPhase === EvaluationPhase.TECHNICAL_COMPLETED ? 'success' :
            record.evaluationPhase === EvaluationPhase.FINANCIAL_IN_PROGRESS ? 'processing' :
            record.evaluationPhase === EvaluationPhase.FINANCIAL_COMPLETED ? 'success' :
            'success'
          }>
            {record.evaluationPhase?.replace(/_/g, ' ') || 'NOT STARTED'}
          </Tag>
          {record.technicalEvaluationMode !== TechnicalEvaluationMode.NONE && false && (
            <Text type="secondary">
              {record.evaluationPhase === EvaluationPhase.TECHNICAL_COMPLETED ||
               record.evaluationPhase === EvaluationPhase.FINANCIAL_IN_PROGRESS ||
               record.evaluationPhase === EvaluationPhase.FINANCIAL_COMPLETED ||
               record.evaluationPhase === EvaluationPhase.COMPLETE
                ? 'Financial evaluation enabled' : 'Complete technical evaluation to enable financial evaluation'}
            </Text>
          )}
        </Space>
      </Card>

      {/* Technical Evaluation Section */}
      {technicalCriteria.length > 0 && viewType !== 'financial' && (
        <Card title={
          <Space>
            <Text>Technical Evaluation</Text>
            {record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL && (
              <Tag color="blue">PASS/FAIL Mode</Tag>
            )}
            {record.technicalEvaluationMode === TechnicalEvaluationMode.MATRIX && (
              <Tag color="purple">MATRIX Mode</Tag>
            )}
          </Space>
        } type="inner">
          <Table
            dataSource={technicalTableData}
            columns={technicalColumns}
            rowKey="criteriaId"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            bordered
          />
        </Card>
      )}

      {/* Financial Evaluation Section */}
      {financialCriteria.length > 0 && viewType !== 'technical' && (
        <Card title={
          <Space>
            <Text>Financial Evaluation</Text>
            {isFinancialCompleted && (
              <Alert
                message="Financial Evaluation Completed"
                description="All inputs are disabled as financial evaluation has been completed."
                type="info"
                showIcon
                style={{ marginLeft: 'auto', maxWidth: '300px' }}
              />
            )}
            {!isFinancialCompleted && isFinancialDisabled && (
              <Alert
                message="Complete technical evaluation first"
                type="warning"
                showIcon
                style={{ marginLeft: 'auto', maxWidth: '300px' }}
              />
            )}
          </Space>
        } type="inner">
          <Table
            dataSource={financialTableData}
            columns={financialColumns}
            rowKey="criteriaId"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            bordered
          />
        </Card>
      )}

      {/* Summary Section - Only show when financial evaluation is completed */}
      {record.evaluationPhase && record.evaluationPhase != EvaluationPhase.NOT_STARTED && (
        <div>
    
        <Card title="Evaluation Summary" type="inner">
          <Table
            dataSource={summaryTableData}
            columns={summaryColumns}
            rowKey="criteriaId"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            bordered
          />
        </Card>
    

      <Card title="Evaluation Legend" size="small">
        <Space direction="vertical">
          <div><Tag color="success">Green</Tag> - Highest score for criteria / Qualified</div>
          <div><Tag color="error">Red</Tag> - Significantly lower score {'<'}80% of highest / Disqualified</div>
          <div><Tag color="warning">Orange</Tag> - Lower score {'<'}90% of highest</div>
          <div><CheckCircleOutlined style={{ color: '#52c41a' }} /> Pass / <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Fail - For PASS_FAIL technical evaluation</div>
          <Text type="secondary">Enter scores (0-100) and remarks for each criteria. Compliance: Toggle to indicate if bid meets requirements. Decision: Select winner/qualified/disqualified. Evaluation metadata shows who evaluated when.</Text>
        </Space>
      </Card>
      </div>)}
    </Space>
  );
};